import { MESApiService, type spParameter } from "@dexteel/mesf-core";
import { getTableRows } from "@/shared/utils/storedProcedureRows";
import moment from "moment";

import { WEIGHT_ITEM_CODE } from "@/shared/models/SectionSpec";
import type {
	RollingDimensionSummary,
	RollingSummary,
	RollingSummaryResult,
} from "../models/RollingSummary";

interface RollingSummaryResponse
	extends Omit<
		RollingSummary,
		| "AvgLbsPerFoot"
		| "MinLbsPerFoot"
		| "MaxLbsPerFoot"
		| "SamplesWithoutWeight"
		| "SinceUtc"
		| "FirstInspectedAt"
		| "LastInspectedAt"
	> {
	SinceUtc: string | null;
	FirstInspectedAt: string | null;
	LastInspectedAt: string | null;
}

export const getRollingSummary = async (
	params: {
		assetId: number;
		sectionId: number;
	},
	signal?: AbortSignal,
): Promise<RollingSummaryResult> => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "AssetId", value: params.assetId },
		{ name: "SectionId", value: params.sectionId },
	];

	const resp = await apiService.callV2(
		"[DIM].[GetRollingSummary]",
		parameters,
		signal,
	);

	if (!resp.ok)
		throw new Error(resp.message || "Error fetching the rolling summary");

	const summary = getTableRows<RollingSummaryResponse>(resp, 0)[0];
	const dimensions = getTableRows<RollingDimensionSummary>(resp, 1);
	const weight = dimensions.find((item) => item.Code === WEIGHT_ITEM_CODE);

	return {
		summary: summary
			? {
					...summary,
					SinceUtc:
						summary.SinceUtc == null
							? null
							: moment.utc(summary.SinceUtc).toDate(),
					FirstInspectedAt:
						summary.FirstInspectedAt == null
							? null
							: moment.utc(summary.FirstInspectedAt).toDate(),
					LastInspectedAt:
						summary.LastInspectedAt == null
							? null
							: moment.utc(summary.LastInspectedAt).toDate(),
					AvgLbsPerFoot: weight?.AvgValue ?? null,
					MinLbsPerFoot: weight?.MinValue ?? null,
					MaxLbsPerFoot: weight?.MaxValue ?? null,
					SamplesWithoutWeight: summary.Samples - (weight?.Samples ?? 0),
				}
			: null,
		dimensions: dimensions.filter((item) => item !== weight),
	};
};
